import type {
  AttributeBoundary,
  GridType,
  MapHeader,
  PaletteBody,
  Range2D,
  ScaleType,
} from '@library/types'
import { convertGlobalGeologicalCoordinatesToLocal } from '@library/utils'
import { vec4 } from 'gl-matrix'
import type { FC } from 'react'

import { useWebGlContext } from '../context'
import { Label } from '../Label'
import { useIsolineLabels } from './hooks'

type MapLabelProps = {
  mapHeader: MapHeader | undefined
  mapBody: GridType['body']
  palette: PaletteBody
  baseIsoline: number
  step: number
  scale: ScaleType
  origin: Range2D
  digitPrecisionValidation: number
  fontSize: number
  thickness: number
  attributeBoundary?: AttributeBoundary
}

export const MapLabel: FC<MapLabelProps> = ({
  mapHeader,
  mapBody,
  palette,
  baseIsoline,
  step,
  scale,
  origin,
  digitPrecisionValidation,
  fontSize,
  thickness,
  attributeBoundary,
}) => {
  const {
    canvas: { value: containerElement },
  } = useWebGlContext()

  const isolinesLabels = useIsolineLabels(
    mapHeader,
    mapBody,
    baseIsoline,
    step,
    containerElement,
    origin,
    scale,
    palette,
    attributeBoundary,
  )

  if (!mapHeader || !isolinesLabels.length) return null

  return (
    <>
      {isolinesLabels.map(({ x, y, z, backgroundColor, atan }) => {
        const text = z.toString()
        const labelId = `label ${text} + ${x} + ${y}`
        const localPoint = convertGlobalGeologicalCoordinatesToLocal(origin, { x, y })

        return (
          <Label
            key={labelId}
            point={localPoint}
            scale={scale}
            rotation={atan + mapHeader.rotationAngle}
            fontSize={fontSize}
            text={text}
            thickness={thickness}
            color={vec4.fromValues(0, 0, 0, 1)}
            horizonAlignment="center"
            verticalAlignment="center"
            fillBackground={true}
            backgroundColor={backgroundColor}
          />
        )
      })}
    </>
  )
}
